/** Batch #14.309 — Paramètres › Pages légales : édition du texte des pages publiques Conditions d'utilisation / Politique de confidentialité. */
import { useCallback, useEffect, useState } from "react";
import { Scale, Save, RotateCcw, ExternalLink, Copy, Eye, Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { LegalText } from "@/pages/public/LegalPage";

const SLUGS = [["conditions-utilisation", "Conditions d'utilisation"], ["politique-confidentialite", "Politique de confidentialité"]];

export default function LegalPagesSection() {
  const [slug, setSlug] = useState(SLUGS[0][0]);
  const [page, setPage] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const publicUrl = `${window.location.origin}/${slug}`;

  const load = useCallback(async () => {
    setPage(null);
    try { const r = await api.get(`/admin/legal/${slug}`); setPage(r.data); setBody(r.data.raw_body || ""); }
    catch { toast.error("Impossible de charger la page"); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const save = async (value) => {
    setBusy(true);
    try { const r = await api.put(`/admin/legal/${slug}`, { body: value }); setPage(r.data); setBody(r.data.raw_body || ""); toast.success(value === null ? "Texte par défaut rétabli" : "Page enregistrée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const reset = () => { if (window.confirm("Rétablir le texte par défaut ? Vos modifications seront perdues.")) save(null); };
  const copy = () => { navigator.clipboard?.writeText(publicUrl); toast.success("Adresse copiée"); };

  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="legal-section">
      <h3 className="font-semibold text-sm flex items-center gap-2"><Scale className="w-4 h-4" /> Pages légales publiques</h3>
      <p className="text-xs text-gray-500">Ces deux pages sont accessibles sans connexion (exigées notamment par Intuit pour QuickBooks). Les marqueurs <code>{"{company}"}</code>, <code>{"{address}"}</code>, <code>{"{email}"}</code>, <code>{"{phone}"}</code> sont remplacés par vos coordonnées des Paramètres.</p>

      <div className="flex gap-2" role="tablist">
        {SLUGS.map(([s, label]) => (
          <button key={s} type="button" onClick={() => { setSlug(s); setPreview(false); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${slug === s ? "bg-[#072354] text-white border-[#072354]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`} data-testid={`legal-tab-${s}`}>{label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <span className="text-slate-500">Adresse publique :</span>
        <code className="font-mono text-slate-800 break-all" data-testid="legal-public-url">{publicUrl}</code>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="legal-copy-url"><Copy className="w-3 h-3" /> Copier</button>
        <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid="legal-open-url"><ExternalLink className="w-3 h-3" /> Ouvrir</a>
      </div>

      {!page ? <div className="text-xs text-gray-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Chargement…</div> : (
        <>
          <div className="flex items-center justify-between text-xs">
            <span className={`px-2 py-0.5 rounded-full font-semibold ${page.is_custom ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="legal-status">{page.is_custom ? `Texte personnalisé · modifié le ${page.updated_at}` : "Texte par défaut"}</span>
            <button type="button" onClick={() => setPreview((v) => !v)} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="legal-preview-toggle"><Eye className="w-3.5 h-3.5" /> {preview ? "Modifier le texte" : "Aperçu"}</button>
          </div>
          {preview ? (
            <div className="border rounded-lg p-4 max-h-[60vh] overflow-y-auto" data-testid="legal-preview"><LegalText body={page.body} className="text-sm" /></div>
          ) : (
            <>
              <textarea rows={22} value={body} onChange={(e) => setBody(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-xs font-mono leading-relaxed" data-testid="legal-body" />
              <p className="text-[10px] text-gray-400">Mise en forme : <code># Titre de section</code> · <code>- élément de liste</code> · <code>**gras**</code> · une ligne vide sépare les paragraphes.</p>
            </>
          )}
          <div className="flex gap-2 justify-end">
            {page.is_custom && <button type="button" onClick={reset} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50" data-testid="legal-reset-btn"><RotateCcw className="w-3.5 h-3.5" /> Rétablir le texte par défaut</button>}
            <button type="button" onClick={() => save(body)} disabled={busy || preview} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#072354] text-white hover:bg-[#0a3270] disabled:opacity-50" data-testid="legal-save-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer</button>
          </div>
        </>
      )}
    </div>
  );
}
